import { useEffect, useRef, useState } from "react";
import Cache from "../utils/Cache";

// Matches the "min(Npx, 95vw)" convention useResizableDrawerWidth already
// uses for width.
const VIEWPORT_MAX_RATIO = 0.95;
const DEFAULT_KEY_STEP = 24;

const clampWidth = (value, min, max) =>
  Math.round(Math.min(Math.max(value, min), max));

// Drag-to-resize state for an element resized from its RIGHT edge — the
// horizontal sibling of useResizableHeight. NOT the same as
// useResizableDrawerWidth: that one grows from the LEFT edge because a
// right-anchored drawer's right edge is pinned to the screen. This is for an
// ordinary inline/modal element with no such anchor, so its right edge is
// the one that actually moves when the user drags. Persists per usage via
// `storageKey`, mirroring the same Cache-backed precedent.
const useResizableWidth = ({
  storageKey,
  defaultWidth,
  minWidth = defaultWidth,
  maxWidth = 1200,
  enabled = true,
  step = DEFAULT_KEY_STEP,
}) => {
  const cacheKey = `frame-width-${storageKey}`;

  const [width, setWidth] = useState(() => {
    const stored = parseInt(Cache.get(cacheKey), 10);
    const effectiveMax = Math.min(
      maxWidth,
      window.innerWidth * VIEWPORT_MAX_RATIO
    );
    return clampWidth(
      Number.isFinite(stored) ? stored : defaultWidth,
      minWidth,
      effectiveMax
    );
  });
  const [isDragging, setIsDragging] = useState(false);

  // Refs, not state: read synchronously inside pointer handlers without
  // waiting on a re-render, and without re-subscribing handlers every drag.
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(width);
  const latestWidthRef = useRef(width);
  const isDraggingRef = useRef(false);

  const handlePointerDown = (event) => {
    if (!enabled) return;
    event.preventDefault(); // no text-selection / native drag ghost
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartX.current = event.clientX;
    dragStartWidth.current = width;
    latestWidthRef.current = width;
    isDraggingRef.current = true;
    setIsDragging(true);
  };

  // The handle sits on the right edge. Moving the pointer right (clientX
  // increases) must grow the element, so delta is current-minus-start.
  const handlePointerMove = (event) => {
    if (!isDraggingRef.current) return; // guards stray hover pointermoves pre-drag
    const max = Math.min(maxWidth, window.innerWidth * VIEWPORT_MAX_RATIO);
    const deltaX = event.clientX - dragStartX.current;
    const next = clampWidth(dragStartWidth.current + deltaX, minWidth, max);
    latestWidthRef.current = next;
    setWidth(next);
  };

  const endDrag = (event) => {
    if (!isDraggingRef.current) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    isDraggingRef.current = false;
    setIsDragging(false);
    // Persist once, at drag-end — not on every pointermove.
    Cache.set(cacheKey, String(latestWidthRef.current));
  };

  const handleKeyDown = (event) => {
    if (!enabled) return;
    const max = Math.min(maxWidth, window.innerWidth * VIEWPORT_MAX_RATIO);
    let next = null;
    if (event.key === "ArrowRight")
      next = clampWidth(width + step, minWidth, max);
    else if (event.key === "ArrowLeft")
      next = clampWidth(width - step, minWidth, max);
    else if (event.key === "Home") next = minWidth;
    else if (event.key === "End") next = max;
    if (next === null) return;
    event.preventDefault();
    latestWidthRef.current = next;
    setWidth(next);
    Cache.set(cacheKey, String(next)); // each keypress is a discrete, complete action
  };

  // Pointer capture releases itself automatically when the capturing element
  // is removed from the DOM, so an unmount mid-drag needs no cleanup there.
  // The one thing that does need cleanup is this global body-style mutation.
  useEffect(() => {
    if (!isDragging) return undefined;
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isDragging]);

  return {
    width,
    isDragging,
    handleProps: {
      enabled,
      width,
      minWidth,
      maxWidth: Math.min(maxWidth, window.innerWidth * VIEWPORT_MAX_RATIO),
      isDragging,
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown: handleKeyDown,
    },
  };
};

export default useResizableWidth;
